import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import {
  ListHeaderCloseButton,
  DebouncedSearchInput,
  ListHeaderSearchButton,
  useScreenWidth,
} from "@msys/ui";
import { Box, Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { difference } from "lodash-es";
import React from "react";
import { Outlet, useLocation, useMatch, useNavigate } from "react-router-dom";
import { useLatest, useUpdateEffect } from "react-use";
import {
  Agreement,
  namedOperations,
  PermissionName,
} from "../../../clients/graphqlTypes.js";
import { useFeature } from "../../../common/FeatureFlags.js";
import { useUserData } from "../../auth/useUserData.js";
import {
  BreadcrumbItem,
  Page,
  PageTopbarItem,
} from "../../commons/layout/Page.js";
import { PageContainer } from "../../commons/layout/PageContainer.js";
import { PageHeader } from "../../commons/layout/PageHeader.js";
import {
  ConfirmModalProps,
  ConfirmProcess,
  ConfirmProcessRef,
} from "../../commons/modals/ConfirmProcess.js";
import { QUOTE_ITEM_TYPES } from "../../constants.js";
import { QuoteAnnotationsBanner } from "../../features/quote-annotations/QuoteAnnotationsBanner.js";
import { ProfitabilityBox } from "../../features/doc-items/boxes/ProfitabilityBox.js";
import { ButtonItemBindingSelectMultiple } from "../../features/doc-items/fields/ButtonItemBindingSelectMultiple.js";
import { ButtonItemTypeSelectMultiple } from "../../features/doc-items/fields/ButtonItemTypeSelectMultiple.js";
import {
  getViewerDecisionRole,
  isTreePreviewItemVisible,
} from "../../features/doc-items/helpers.js";
import type { ItemChange } from "../../features/doc-items/hooks/useItemBindings.js";
import { useItemsSearch } from "../../features/doc-items/hooks/useItemsSearch.js";
import { ItemSearchChips } from "../../features/doc-items/ItemSearchChips.js";
import { DecisionsInBulkProcessRef } from "../../features/doc-items/modals/DecisionsInBulkProcess.js";
import {
  EstimatedCalculationEmptyRow,
  EstimatedCalculationHeaderRow,
} from "../../features/doc-items/tables/EstimatedCalculationRow.js";
import {
  createTreeItemInput,
  createTreeTableItem,
} from "../../features/projects/quote-trees.js";
import {
  allowHaveChildren,
  shouldRenderCreateInput,
} from "../../features/quotes/helpers.js";
import { QuoteAction } from "../../features/quotes/QuoteAction.js";
import { QuoteDecisionsInBulkProcess } from "../../features/quotes/QuoteDecisionsInBulkProcess.js";
import { QuotePriceSummaryBox } from "../../features/quotes/QuotePriceSummaryBox.js";
import { QuoteSubHeader } from "../../features/quotes/QuoteSubHeader.js";
import { QuoteTimeSummaryBox } from "../../features/quotes/QuoteTimeSummaryBox.js";
import { useCreateQuoteItems } from "../../features/quotes/useCreateQuoteItems.js";
import { useHasSapS4HanaIntegration } from "../../features/sap-s4-hana/hooks.js";
import { VanMarckeProfitability } from "../../features/van-marcke-profitability/VanMarckeProfitability.js";
import { ROOT_ITEM_ID } from "../../trees-virtual/helpers.js";
import { useEnrichExpandedStoreWithParentPathIds } from "../../trees-virtual/hooks/useEnrichExpandedStoreWithParentPathIds.js";
import { useExpandedStoreWithLocalStorage } from "../../trees-virtual/hooks/useExpandedStore.js";
import {
  AdditionalItemInput,
  TreeViewMode,
} from "../../trees-virtual/types.js";
import { VirtualTableTree } from "../../trees-virtual/VirtualTableTree.js";
import { useMoveTreeItem } from "../../trees-virtual/hooks/useMoveTreeItem.js";
import { TreeToggleAllExpandedButton } from "../../trees/components/TreeToggleButton.js";
import {
  createRecursiveTryFocusElementFunction,
  isInTestMode,
} from "../../utils.js";
import {
  ProjectQuoteCalculation_ItemFragment,
  useProjectQuoteCalculationQuery,
} from "./ProjectQuoteCalculation.generated.js";
import { useProjectQuoteContext } from "./ProjectQuoteContext.js";

const REFETCH_QUERIES = [namedOperations.Query.ProjectQuoteCalculation];

interface Props {
  projectId: string;
  quoteId: string;
  prefixBreadcrumbs: BreadcrumbItem[];
  submenuItems: PageTopbarItem[];
  activeSubmenuItem: PageTopbarItem | undefined;
}

export const ProjectQuoteCalculation = ({
  projectId,
  quoteId,
  submenuItems,
  activeSubmenuItem,
  prefixBreadcrumbs,
}: Props) => {
  const pathToDocList = `/projects/${projectId}/quotes`;
  const pathToDoc = `/projects/${projectId}/quotes/${quoteId}`;
  const pathToDocPage = `${pathToDoc}/calculation`;
  const match = useMatch(`${pathToDocPage}/items/:itemId`);
  const selectedItemId = match?.params?.itemId ?? null;
  const navigate = useNavigate();
  const location = useLocation();
  const latestLocation = useLatest(location);

  const [additionalItemInput, setAdditionalItemInput] =
    React.useState<AdditionalItemInput | null>(null);
  const additionalItemInputRef = useLatest(additionalItemInput);
  // set when an item is created from a positioned input — the clearing effect
  // re-anchors a new positioned input below it instead of clearing
  const pendingReanchorItemIdRef = React.useRef<string | null>(null);

  const onAdditionalItemInputChange = React.useCallback(
    (input: AdditionalItemInput) => {
      setAdditionalItemInput(input);
      setTimeout(() => {
        const inputRank =
          input.itemRank + (input.position === "below" ? 0.5 : -0.5);
        if (!isInTestMode())
          createRecursiveTryFocusElementFunction(
            `input#msys-table-input-${input.itemParentType}-${input.itemParentId}[data-node-rank="${inputRank}"]:enabled`
          )();
      });
    },
    []
  );

  const viewer = useUserData().currentUser!;
  const { t } = useTranslate(["QuoteEdit", "Global", "Quote"]);

  const isClientVisibilityEnabled = useFeature("ClientVisibility");

  const [treeContainer, setTreeContainer] =
    React.useState<HTMLDivElement | null>(null);

  const { isMinTablet } = useScreenWidth();

  const [moveTreeItem, moveTreeItemLoading] = useMoveTreeItem({
    projectId,
    docId: quoteId,
  });

  const expandedStore = useExpandedStoreWithLocalStorage(
    "quote",
    quoteId,
    selectedItemId
  );

  const client = useApolloClient();
  const query = useProjectQuoteCalculationQuery({
    client,
    variables: {
      projectId,
      quoteId,
      expandedItemIds: expandedStore.expandedItemIds,
    },
  });

  const project = getDataOrNull(query.data?.project)?.project;
  const doc = getDataOrNull(query.data?.quote)?.quote;
  const docTitle = doc?.title ?? "";

  const navigateToItem = React.useCallback(
    (id: string) =>
      navigate(
        {
          pathname: `${pathToDocPage}/items/${id}`,
          search: latestLocation.current.search,
        },
        { replace: true }
      ),
    [latestLocation, navigate, pathToDocPage]
  );
  const navigateToDocPage = React.useCallback(
    () =>
      navigate(
        { pathname: pathToDocPage, search: latestLocation.current.search },
        { replace: true }
      ),
    [latestLocation, navigate, pathToDocPage]
  );

  const {
    filterItemSearch,
    filters,
    setFilters,
    resetFilters,
    search,
    setSearch,
    searchRef,
    toggleSearchVisible,
    searchVisible,
    isSearchOrFiltersEnabled,
    parentItemIds,
    isItemMuted,
  } = useItemsSearch({
    projectId,
    docId: quoteId,
    // when search is getting applied - we need to open tree to all found items
    onSearchApplied: ({ parentItemIds }) => {
      if (
        difference(parentItemIds, expandedStore.expandedItemIds ?? []).length >
        0
      ) {
        expandedStore.setItemsExpanded(parentItemIds, true);
      }
    },
  });

  const showExpandButton = React.useCallback(
    (item: ProjectQuoteCalculation_ItemFragment) => {
      if (!isSearchOrFiltersEnabled) return item.hasChildren;
      return item.hasChildren && parentItemIds.includes(item.id);
    },
    [isSearchOrFiltersEnabled, parentItemIds]
  );

  const quote = getDataOrNull(query.data?.quote)?.quote;
  const viewerPermissions: PermissionName[] = React.useMemo(
    () => quote?.viewerPermissions ?? [],
    [quote?.viewerPermissions]
  );

  const agreement: Agreement | undefined = doc?.agreement;
  const contractType = doc?.contractType;

  const viewerDecisionRole = getViewerDecisionRole(doc?.actors ?? []);

  const confirmProcessRef = React.useRef<ConfirmProcessRef>(null);
  const startConfirmProcess = React.useCallback((props: ConfirmModalProps) => {
    return confirmProcessRef.current!.startConfirmProcess(props);
  }, []);

  const decisionsInBulkProcessRef =
    React.useRef<DecisionsInBulkProcessRef>(null);
  const startDecisionsInBulkProcess = React.useCallback(() => {
    decisionsInBulkProcessRef.current?.startDecisionsInBulkProcess();
  }, []);

  const { selectWizardContextButton, refetchWizardQuestions } =
    useProjectQuoteContext();

  const onItemCreated = React.useCallback(
    async (newItem?: { id: string }) => {
      // if the item was created from a positioned input, remember it so the
      // clearing effect can re-open a positioned input below it
      if (additionalItemInputRef.current && newItem) {
        pendingReanchorItemIdRef.current = newItem.id;
      }
      await refetchWizardQuestions();
    },
    [additionalItemInputRef, refetchWizardQuestions]
  );

  const {
    createItem,
    createItemByType,
    createItemFromTemplate,
    createItemsFromTemplates,
    createItemsWithProducts,
    pasteItem,
    isAllowedToPasteItem,
  } = useCreateQuoteItems({
    projectId,
    quoteId,
    expandedItemIds: expandedStore.expandedItemIds,
    onItemCreated,
    source: "calculation",
  });

  const TreeItem = React.useMemo(
    () =>
      agreement !== undefined && contractType && quote?.rootItem.id
        ? createTreeTableItem({
            pathToDocPage,
            projectId,
            doc: {
              id: quoteId,
              rootItemId: quote.rootItem.id,
              contractType: contractType,
              viewerPermissions,
              owningSystemOrganisationId: quote.owningSystemOrganisationId,
            },
            docAgreement: agreement,
            startConfirmProcess,
            navigateToItem,
            expandedItemIds: expandedStore.expandedItemIds,
            setItemExpanded: expandedStore.setItemExpanded,
            pasteItem,
            isAllowedToPasteItem,
            viewerDecisionRole,
            onAdditionalItemInputChange,
            isItemMuted: itemProps => isItemMuted(itemProps.item),
          })
        : () => null,
    [
      agreement,
      contractType,
      quote?.rootItem.id,
      quote?.owningSystemOrganisationId,
      pathToDocPage,
      projectId,
      quoteId,
      viewerPermissions,
      startConfirmProcess,
      navigateToItem,
      expandedStore.expandedItemIds,
      expandedStore.setItemExpanded,
      pasteItem,
      isAllowedToPasteItem,
      viewerDecisionRole,
      onAdditionalItemInputChange,
      isItemMuted,
    ]
  );

  const TreeItemInput = React.useMemo(
    () =>
      createTreeItemInput({
        viewMode: TreeViewMode.Table,
        quoteId,
        createItem,
        createItemByType,
        createItemFromTemplate,
        createItemsFromTemplates,
        createItemsWithProducts,
        pasteItem,
        isAllowedToPasteItem,
      }),
    [
      createItem,
      createItemByType,
      createItemFromTemplate,
      createItemsFromTemplates,
      createItemsWithProducts,
      isAllowedToPasteItem,
      pasteItem,
      quoteId,
    ]
  );

  const [filteredTreeItems, rootItem, allDocItems] = React.useMemo(() => {
    if (!doc) return [[], undefined, []];
    const allDocItems = doc.items;
    const rootItem = allDocItems?.find(item => item.isRootItem);
    const treeItems = allDocItems
      .filter(item => item.authorOrganisationId === viewer.organisation.id)
      .filter(item => !item.isAnyParentItemEliminated);
    const filteredItems = treeItems.filter(filterItemSearch).map(item => {
      if (!item.isRootItem) return item;
      return { ...item, parentId: ROOT_ITEM_ID };
    });
    return [filteredItems, rootItem, allDocItems];
  }, [doc, filterItemSearch, viewer.organisation.id]);

  useUpdateEffect(() => {
    const reanchorItemId = pendingReanchorItemIdRef.current;
    if (reanchorItemId) {
      const newItem = allDocItems?.find(i => i.id === reanchorItemId);
      const newItemParent =
        newItem?.parentId != null
          ? allDocItems?.find(i => i.id === newItem.parentId)
          : null;
      if (newItem && newItem.parentId && newItemParent) {
        // re-open a positioned input below the just-created item to keep adding
        pendingReanchorItemIdRef.current = null;
        onAdditionalItemInputChange({
          itemId: newItem.id,
          itemParentId: newItem.parentId,
          itemParentType: newItemParent.type,
          itemRank: newItem.rank,
          position: "below",
        });
      }
      // keep the pending id until the re-anchor succeeds so a later
      // allDocItems update (once the new item lands) can retry
      return;
    }
    setAdditionalItemInput(null);
  }, [selectedItemId, allDocItems, onAdditionalItemInputChange]);

  // redirect to calculation route when selected item is filtered out
  React.useEffect(() => {
    if (
      !query.loading &&
      selectedItemId &&
      !filteredTreeItems.some(item => item.id === selectedItemId) &&
      selectedItemId !== rootItem?.id
    ) {
      navigateToDocPage();
    }
  }, [
    selectedItemId,
    filteredTreeItems,
    pathToDocPage,
    rootItem,
    query.loading,
    navigateToDocPage,
  ]);

  useEnrichExpandedStoreWithParentPathIds(
    expandedStore,
    allDocItems,
    selectedItemId,
    !query.loading && !query.error
  );

  const filterItem = React.useCallback(
    (i: ProjectQuoteCalculation_ItemFragment) =>
      isTreePreviewItemVisible(true, i, allDocItems, doc?.isBinding ?? true),
    [allDocItems, doc?.isBinding]
  );

  const isRootSelected = selectedItemId === rootItem?.id;
  const isAgreed =
    (doc?.isPublished && doc?.isBinding && doc?.agreement === "YES") ?? false;
  const isViewersQuote =
    doc?.owningSystemOrganisationId === viewer.organisation.id;

  const EmptyRow = React.useMemo(() => <EstimatedCalculationEmptyRow />, []);
  const HeaderRow = React.useMemo(() => <EstimatedCalculationHeaderRow />, []);

  const enableCreating = isViewersQuote ? true : false;
  const enableDragging = isViewersQuote ? !isAgreed : false;
  const enableNavigating = isViewersQuote ? true : false;

  const hasSapS4HanaIntegration = useHasSapS4HanaIntegration();

  return (
    <Page
      subtitle={project?.title}
      title={docTitle}
      submenuItems={submenuItems}
      breadcrumbs={prefixBreadcrumbs}
      header={
        <PageHeader
          breadcrumbs={prefixBreadcrumbs}
          submenuItems={submenuItems}
          activeSubmenuItem={activeSubmenuItem}
        />
      }
      subHeader={
        project && doc ? (
          <QuoteSubHeader
            project={project}
            quote={doc}
            activeView="calculation"
            canEdit={isViewersQuote}
            isReadOnly={isAgreed}
            pathToDocList={pathToDocList}
            pathToDoc={pathToDoc}
            hasSapS4HanaIntegration={hasSapS4HanaIntegration}
            isHeaderVisible={undefined as never}
            setHeaderVisible={undefined as never}
            expandedItemIds={expandedStore.expandedItemIds}
          />
        ) : undefined
      }
      action={
        project &&
        doc && (
          <QuoteAction
            doc={doc}
            project={project}
            onDiscardPendingChangesRefetchQueries={REFETCH_QUERIES}
            onDeclineChangesRefetchQueries={REFETCH_QUERIES}
            startDecisionsInBulkProcess={startDecisionsInBulkProcess}
            expandedItemIds={expandedStore.expandedItemIds}
            viewerDecisionRole={viewerDecisionRole}
          />
        )
      }
    >
      {doc && project && (
        <PageContainer
          $display="block"
          ref={isMinTablet ? setTreeContainer : undefined}
          $noPadding
        >
          <Stack
            direction="column"
            spacing={2}
            sx={{
              py: 2,
            }}
          >
            <QuoteAnnotationsBanner sx={{ px: 2 }} />
            <Stack
              direction="row"
              spacing={1}
              sx={{
                width: "100%",
                alignItems: "center",
                px: 2,
              }}
            >
              <TreeToggleAllExpandedButton
                areAllItemsExpanded={expandedStore.areAllItemsExpanded}
                setAllItemsExpanded={expandedStore.setAllItemsExpanded}
              />
              <ButtonItemTypeSelectMultiple
                value={filters.type ?? []}
                onChange={value => {
                  setFilters(f => ({
                    ...f,
                    type: value.length > 0 ? value : undefined,
                  }));
                }}
              />
              <ButtonItemBindingSelectMultiple
                value={[
                  ...(filters.binding ?? []),
                  ...(filters.needsAgreement ? ["change" as ItemChange] : []),
                ]}
                onChange={value => {
                  const bindings = value.filter(v => v !== "change");
                  const needsAgreement = value.some(v => v === "change");
                  setFilters(f => ({
                    ...f,
                    binding: bindings.length > 0 ? bindings : undefined,
                    needsAgreement: needsAgreement || undefined,
                  }));
                }}
                hasAgreement={doc?.agreement === "YES"}
              />
              {selectWizardContextButton}
              {!isMinTablet && !searchVisible && (
                <ListHeaderSearchButton onClick={toggleSearchVisible} />
              )}
              {isMinTablet && (
                <DebouncedSearchInput
                  inputRef={searchRef}
                  placeholder={t("Search", { ns: "Global" })}
                  defaultValue={search}
                  onChangeSearchTerm={setSearch}
                />
              )}
            </Stack>

            <ItemSearchChips
              filters={filters}
              setFilters={setFilters}
              onReset={resetFilters}
              sx={{ px: 2 }}
            />

            {!isMinTablet && searchVisible && (
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  width: "100%",
                  alignItems: "center",
                  px: 2,
                }}
              >
                <DebouncedSearchInput
                  inputRef={searchRef}
                  placeholder={t("Search", { ns: "Global" })}
                  defaultValue={search}
                  onChangeSearchTerm={setSearch}
                />
                <ListHeaderCloseButton onClick={toggleSearchVisible} />
              </Stack>
            )}

            {filteredTreeItems.length > 0 ? (
              <VirtualTableTree<ProjectQuoteCalculation_ItemFragment>
                minWidth={1540}
                moveTreeItem={enableDragging ? moveTreeItem : undefined}
                moveTreeItemLoading={moveTreeItemLoading}
                items={filteredTreeItems}
                allItems={allDocItems}
                rootItemId={ROOT_ITEM_ID}
                filterItem={isClientVisibilityEnabled ? filterItem : undefined}
                selectedItemId={selectedItemId}
                enableCreating={enableCreating}
                documentItemTypes={QUOTE_ITEM_TYPES}
                container={treeContainer}
                itemComponent={TreeItem}
                inputComponent={TreeItemInput}
                EmptyRow={EmptyRow}
                HeaderRow={HeaderRow}
                headerTitle={t("Title", { ns: "QuoteEdit" })}
                allowHaveChildren={allowHaveChildren}
                shouldRenderCreateInput={shouldRenderCreateInput}
                expandedStore={expandedStore}
                additionalItemInput={additionalItemInput}
                showExpandButton={showExpandButton}
              />
            ) : (
              <Box
                sx={{
                  height: 2,
                }}
              />
            )}

            <QuoteDecisionsInBulkProcess
              ref={decisionsInBulkProcessRef}
              projectId={projectId}
              quoteId={doc.id}
              canFinalize={true}
              externalExpandedItemIds={expandedStore.expandedItemIds}
            />

            {doc.proposedCalculation && (
              <Box
                sx={{
                  px: 2,
                }}
              >
                <QuoteTimeSummaryBox
                  key={`quote_time_summary-${isAgreed}`}
                  calculated={doc.proposedCalculation}
                />
              </Box>
            )}

            {doc.proposedCalculation && (
              <Box
                sx={{
                  px: 2,
                }}
              >
                <QuotePriceSummaryBox
                  key={`quote_price_summary-${isAgreed}`}
                  isManualSave={isAgreed}
                  quoteId={quoteId}
                  quote={doc}
                  project={project}
                  discountPercentage={doc.discountPercentage}
                  calculated={doc.proposedCalculation}
                />
              </Box>
            )}

            {doc.proposedCalculation && (
              <Box
                sx={{
                  px: 2,
                }}
              >
                <ProfitabilityBox calculated={doc.proposedCalculation} />
              </Box>
            )}

            <VanMarckeProfitability
              projectId={projectId}
              docId={quoteId}
              stackProps={{ sx: { px: 2 } }}
            />
          </Stack>
        </PageContainer>
      )}
      <ConfirmProcess ref={confirmProcessRef} />
      <Outlet
        context={{
          expandedItemIds: expandedStore.expandedItemIds,
          CreateChildComponent: TreeItemInput,
          pasteItem,
          isAllowedToPasteItem,
          viewerDecisionRole,
        }}
      />
    </Page>
  );
};
